import React, { useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useLanguage } from '../../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, X, Check, Tag, FlaskConical,
  MapPin, AlertTriangle
} from 'lucide-react';
import { COMMON_UNITS, ALL_CATEGORIES } from '../../../hooks/useAdminData';
import { IMPACT_TYPES_CONFIG } from '../../../hooks/useAdminFactors';

const FactorFormModal = ({
  show, onClose,
  editingFactor,
  factorForm, setFactorForm,
  subcategories,
  onSubcategoryChange,
  onAddInputUnit, onRemoveInputUnit,
  getLinkedCategories,
  onSave
}) => {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();

  const isValid = factorForm.name_simple_fr &&
    factorForm.subcategory &&
    factorForm.impacts.length > 0 &&
    !factorForm.impacts.some(i =>
      i.value === '' || i.value === null || i.value === undefined
      || !i.unit);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-3xl rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-hidden flex flex-col`}
          >
            {/* Header */}
            <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-blue-500" />
                  {editingFactor ? t('admin.factors.edit') : t('admin.factors.new')}
                </h3>
                <button onClick={onClose} className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Simplified names */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>{t('admin.factors.displayNamesSection')}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.nameSimpleFr')}</label>
                    <input
                      type="text"
                      data-testid="input-name-simple-fr"
                      value={factorForm.name_simple_fr}
                      onChange={(e) => setFactorForm(prev => ({ ...prev, name_simple_fr: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                      placeholder="Diesel — Véhicules légers"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.nameSimpleDe')}</label>
                    <input
                      type="text"
                      data-testid="input-name-simple-de"
                      value={factorForm.name_simple_de}
                      onChange={(e) => setFactorForm(prev => ({ ...prev, name_simple_de: e.target.value }))}
                      className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                      placeholder="Diesel — Leichte Fahrzeuge"
                    />
                  </div>
                </div>
              </div>

              {/* Source BAFU */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.sourceBafuLabel')}</label>
                <input
                  type="text"
                  data-testid="input-source-product-name"
                  value={factorForm.source_product_name}
                  onChange={(e) => setFactorForm(prev => ({ ...prev, source_product_name: e.target.value }))}
                  className={`w-full px-4 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                  placeholder="Light fuel oil, burned in boiler 100kW condensing {CH} MJ"
                />
              </div>

              {/* W6-P4 : section "Noms techniques (import)" name_fr/de retirée —
                  édition via name_simple_fr/de (ci-dessus) + nom source BAFU. */}

              {/* Subcategory & Default Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.subcategory')} *</label>
                  <select
                    value={factorForm.subcategory}
                    onChange={(e) => onSubcategoryChange(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="">{t('common.selectOption')}</option>
                    {subcategories.map(s => (
                      <option key={s.code} value={s.code}>{language === 'fr' ? s.name_fr : (s.name_de || s.name_fr)} ({s.code})</option>
                    ))}
                  </select>
                  {factorForm.subcategory && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('admin.factors.linkedCategories')}: {getLinkedCategories().join(', ') || t('common.none')}
                    </p>
                  )}
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.defaultUnit')}</label>
                  <select
                    value={factorForm.default_unit}
                    onChange={(e) => setFactorForm(prev => ({ ...prev, default_unit: e.target.value }))}
                    className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="">{t('common.selectOption')}</option>
                    {COMMON_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              {/* Alternative subcategories */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('admin.factors.alternativeSubcategories')} <span className={`font-normal ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{t('admin.factors.alternativeSubcategoriesOptional')}</span>
                </label>
                <p className={`text-xs mb-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {t('admin.factors.alternativeSubcategoriesDesc')}
                </p>
                <div className={`max-h-40 overflow-y-auto rounded-lg border p-2 space-y-0.5 ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  {subcategories.filter(s => s.code !== factorForm.subcategory).map(s => (
                    <label key={s.code} className={`flex items-center gap-2 text-sm px-2 py-1 rounded cursor-pointer ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-100'}`}>
                      <input
                        type="checkbox"
                        checked={factorForm.alternative_subcategories.includes(s.code)}
                        onChange={(e) => {
                          const code = s.code;
                          setFactorForm(prev => ({
                            ...prev,
                            alternative_subcategories: e.target.checked
                              ? [...prev.alternative_subcategories, code]
                              : prev.alternative_subcategories.filter(c => c !== code)
                          }));
                        }}
                        className="accent-blue-500"
                      />
                      <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>{language === 'fr' ? s.name_fr : (s.name_de || s.name_fr)}</span>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>({s.code})</span>
                    </label>
                  ))}
                  {subcategories.filter(s => s.code !== factorForm.subcategory).length === 0 && (
                    <p className={`text-xs text-center py-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{t('admin.factors.selectMainSubcategoryFirst')}</p>
                  )}
                </div>
                {(() => {
                  if (!factorForm.alternative_subcategories?.length || !factorForm.impacts?.length) return null;
                  const activeImpactKeys = new Set(factorForm.impacts.map(i => i.impactKey));
                  const scopeLabels = { scope1: 'Scope 1', scope2: 'Scope 2', scope3_3: 'Scope 3.3', scope3: 'Scope 3' };
                  const warnings = factorForm.alternative_subcategories.flatMap(altCode => {
                    const altSubcat = subcategories.find(s => s.code === altCode);
                    if (!altSubcat) return [];
                    const neededKeys = new Set();
                    for (const catCode of (altSubcat.categories || [])) {
                      const cat = ALL_CATEGORIES.find(c => c.value === catCode);
                      if (!cat) continue;
                      if (catCode === 'activites_combustibles_energie') neededKeys.add('scope3_3');
                      else if (cat.scope === 'scope1') neededKeys.add('scope1');
                      else if (cat.scope === 'scope2') neededKeys.add('scope2');
                      else if (cat.scope === 'scope3_amont' || cat.scope === 'scope3_aval') neededKeys.add('scope3');
                    }
                    const missing = [...neededKeys].filter(k => !activeImpactKeys.has(k));
                    if (!missing.length) return [];
                    return [{ name: language === 'fr' ? altSubcat.name_fr : (altSubcat.name_de || altSubcat.name_fr), missing: missing.map(k => scopeLabels[k]).join(', ') }];
                  });
                  if (!warnings.length) return null;
                  return (
                    <div className={`mt-2 p-3 rounded-lg flex gap-2 ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className={`text-xs font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                          {t('admin.factors.alternativeSubcategoriesWarning')}
                        </p>
                        <ul className={`text-xs mt-1 space-y-0.5 ${isDark ? 'text-amber-300/80' : 'text-amber-600'}`}>
                          {warnings.map((w, i) => (
                            <li key={i}>• <strong>{w.name}</strong> — {t('admin.factors.requires')} {w.missing}</li>
                          ))}
                        </ul>
                        <p className={`text-xs mt-1 ${isDark ? 'text-amber-400/60' : 'text-amber-500'}`}>
                          {t('admin.factors.zeroImpactWarning')}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Reporting method & Popularity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    <MapPin className="w-4 h-4 inline mr-1" />{t('admin.factors.reportingMethod')}
                  </label>
                  <select
                    data-testid="select-reporting-method"
                    value={factorForm.reporting_method}
                    onChange={(e) => setFactorForm(prev => ({ ...prev, reporting_method: e.target.value }))}
                    className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="">{t('common.notDefined')}</option>
                    <option value="location">Location-based</option>
                    <option value="market">Market-based</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {t('admin.factors.popularity').replace('{n}', factorForm.popularity_score)}
                  </label>
                  <input
                    type="range"
                    data-testid="input-popularity"
                    min="0"
                    max="100"
                    value={factorForm.popularity_score}
                    onChange={(e) => setFactorForm(prev => ({ ...prev, popularity_score: parseInt(e.target.value) }))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <div className={`flex justify-between text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    <span>{t('admin.factors.rare')}</span>
                    <span>{t('admin.factors.common')}</span>
                  </div>
                </div>
              </div>

              {/* Input Units */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.inputUnits')}</label>
                <div className="space-y-2">
                  {factorForm.input_units.map((unit, i) => (
                    <div key={`unit-${i}-${unit}`} className="flex items-center gap-2">
                      <select
                        value={unit}
                        onChange={(e) => setFactorForm(prev => ({
                          ...prev,
                          input_units: prev.input_units.map((u, idx) => idx === i ? e.target.value : u)
                        }))}
                        className={`flex-1 px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                      >
                        <option value="">{t('common.selectOption')}</option>
                        {COMMON_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      {factorForm.input_units.length > 1 && (
                        <button onClick={() => onRemoveInputUnit(i)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={onAddInputUnit} className="text-sm text-blue-500 hover:underline">+ {t('admin.factors.addUnit')}</button>
                </div>
              </div>

              {/* Impacts */}
              <ImpactsSection
                factorForm={factorForm}
                setFactorForm={setFactorForm}
              />

              {/* Tags & Metadata */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-3">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    <Tag className="w-4 h-4 inline mr-1" />{t('admin.factors.tagsSeparated')}
                  </label>
                  <input
                    type="text"
                    value={factorForm.tags}
                    onChange={(e) => setFactorForm(prev => ({ ...prev, tags: e.target.value }))}
                    className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                    placeholder={t('admin.factors.tagsPlaceholder')}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.source')}</label>
                  <input type="text" value={factorForm.source} onChange={(e) => setFactorForm(prev => ({ ...prev, source: e.target.value }))} className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.region')}</label>
                  <input type="text" value={factorForm.region} onChange={(e) => setFactorForm(prev => ({ ...prev, region: e.target.value }))} className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{t('admin.factors.year')}</label>
                  <input type="number" value={factorForm.year} onChange={(e) => setFactorForm(prev => ({ ...prev, year: e.target.value }))} className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`} />
                </div>
              </div>

              {/* Expert toggle */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${
                !factorForm.is_public
                  ? isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
                  : isDark ? 'bg-slate-700/30 border-slate-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center gap-3">
                  <FlaskConical className={`w-5 h-5 ${!factorForm.is_public ? 'text-amber-500' : isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  <div>
                    <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                      {t('admin.factors.expertFactor')}
                    </span>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('admin.factors.expertFactorDesc')}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="toggle-is-public"
                  onClick={() => setFactorForm(prev => ({ ...prev, is_public: !prev.is_public }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    !factorForm.is_public ? 'bg-amber-500' : isDark ? 'bg-slate-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    !factorForm.is_public ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className={`p-6 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className="flex gap-3">
                <button onClick={onClose} className={`flex-1 px-4 py-3 rounded-xl border ${isDark ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                  Annuler
                </button>
                <button
                  onClick={onSave}
                  disabled={!isValid}
                  className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  {editingFactor ? t('common.edit') : t('admin.factors.create')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


const ALL_SCOPES = ['scope1', 'scope2', 'scope3_3', 'scope3'];

const colorClasses = {
  scope1: 'border-blue-500/50 bg-blue-500/5',
  scope2: 'border-cyan-500/50 bg-cyan-500/5',
  scope3_3: 'border-amber-500/50 bg-amber-500/5',
  scope3: 'border-purple-500/50 bg-purple-500/5',
};
const headerColors = {
  scope1: 'bg-blue-500',
  scope2: 'bg-cyan-500',
  scope3_3: 'bg-amber-500',
  scope3: 'bg-purple-500',
};

/* Sub-component for the impacts section inside the form */
const ImpactsSection = ({ factorForm, setFactorForm }) => {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  // Draft holds uncommitted field values for scopes that have no impact yet
  const [drafts, setDrafts] = useState({});

  const getImpact = (scopeKey) => factorForm.impacts.find(i => i.impactKey === scopeKey);

  const handleFieldChange = (scopeKey, field, rawValue) => {
    const existing = getImpact(scopeKey);

    if (field === 'value') {
      const num = parseFloat(rawValue);
      const isEmpty = !rawValue || isNaN(num);

      if (isEmpty) {
        setDrafts(prev => ({ ...prev, [scopeKey]: { ...prev[scopeKey], value: rawValue } }));
        if (existing) {
          setFactorForm(prev => ({ ...prev, impacts: prev.impacts.filter(i => i.impactKey !== scopeKey) }));
        }
        return;
      }

      if (existing) {
        setFactorForm(prev => ({
          ...prev,
          impacts: prev.impacts.map(i => i.impactKey !== scopeKey ? i : { ...i, value: rawValue }),
        }));
      } else {
        const config = IMPACT_TYPES_CONFIG.find(c => c.key === scopeKey);
        const draft = drafts[scopeKey] || {};
        const newImpact = {
          impactKey: scopeKey,
          scope: scopeKey,
          ...(scopeKey === 'scope3_3' && { category: 'activites_combustibles_energie' }),
          value: rawValue,
          unit: draft.unit || 'kgCO2e/',
          type: config?.type || 'direct',
        };
        setFactorForm(prev => ({ ...prev, impacts: [...prev.impacts, newImpact] }));
        setDrafts(prev => ({ ...prev, [scopeKey]: {} }));
      }
      return;
    }

    // category / unit: update impact if active, otherwise store in draft
    if (existing) {
      setFactorForm(prev => ({
        ...prev,
        impacts: prev.impacts.map(i => i.impactKey !== scopeKey ? i : { ...i, [field]: rawValue }),
      }));
    } else {
      setDrafts(prev => ({ ...prev, [scopeKey]: { ...prev[scopeKey], [field]: rawValue } }));
    }
  };

  return (
    <div>
      <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
        Impacts par scope
        {factorForm.impacts.length > 0 && (
          <span className="ml-2 text-purple-500 font-normal">
            <Sparkles className="w-4 h-4 inline" /> {t('admin.factors.impactsDefined').replace('{n}', factorForm.impacts.length)}
          </span>
        )}
      </label>

      <div className="space-y-4">
        {ALL_SCOPES.map(scopeKey => {
          const config = IMPACT_TYPES_CONFIG.find(c => c.key === scopeKey);
          const impact = getImpact(scopeKey);
          const draft = drafts[scopeKey] || {};
          const hasImpact = !!impact;

          const currentValue = hasImpact ? (impact.value?.toString() || '') : (draft.value || '');
          const currentUnit = hasImpact ? (impact.unit || 'kgCO2e/') : (draft.unit || 'kgCO2e/');

          return (
            <div
              key={scopeKey}
              className={`rounded-xl border-2 overflow-hidden transition-opacity ${colorClasses[scopeKey]} ${!hasImpact ? 'opacity-55' : ''}`}
            >
              <div className={`px-4 py-2 ${headerColors[scopeKey]} text-white`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{config ? t(config.labelKey) : scopeKey}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-80">{config ? t(config.descKey) : ''}</span>
                    {!hasImpact && <span className="text-xs opacity-70 italic">— optionnel</span>}
                  </div>
                </div>
              </div>
              <div className="p-4">
                {scopeKey === 'scope3_3' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {t('admin.factors.category')}
                      </label>
                      <div className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>
                        {t('admin.factors.fuelEnergyActivities33')}
                      </div>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {t('admin.factors.valueKgCO2e')}{hasImpact ? ' *' : ''}
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={currentValue}
                        onChange={(e) => handleFieldChange(scopeKey, 'value', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                        placeholder={hasImpact ? t('admin.factors.valueExamplePlaceholder') : t('admin.factors.valueEmptyHint')}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {t('admin.factors.fullUnit')}{hasImpact ? ' *' : ''}
                      </label>
                      <input
                        type="text"
                        value={currentUnit}
                        onChange={(e) => handleFieldChange(scopeKey, 'unit', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                        placeholder={t('admin.factors.unitExamplePlaceholder')}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {t('admin.factors.valueKgCO2e')}{hasImpact ? ' *' : ''}
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={currentValue}
                        onChange={(e) => handleFieldChange(scopeKey, 'value', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                        placeholder={hasImpact ? t('admin.factors.valueExamplePlaceholder') : t('admin.factors.valueEmptyHint')}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {t('admin.factors.fullUnit')}{hasImpact ? ' *' : ''}
                      </label>
                      <input
                        type="text"
                        value={currentUnit}
                        onChange={(e) => handleFieldChange(scopeKey, 'unit', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}
                        placeholder={t('admin.factors.unitExamplePlaceholder')}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FactorFormModal;
